/*
 * Einen OEM-Datenpunkt ändern (docs/servicezugang-oem-und-fern.md): alt → neu,
 * gerundet auf die Stufe des Reglers, mit Grenzen, Auswahl oder Ein/Aus.
 * Geschrieben wird über den Fernzugang; danach zeigt das Fenster, was der
 * Regler zurückmeldet, und bietet die Rücknahme an. Jede Schreibung steht im
 * Schreibprotokoll am Panel und in der Befehls-Historie.
 */
import { useState } from 'preact/hooks';
import { createPortal } from 'preact/compat';
import { errorText, serviceCall } from './serviceRemoteApi';
import {
  checkValue, displayValue, formatPlain, rawValue, stepText, valueToWrite, withComma,
  type ServicePoint, type ServiceReading, type ServiceWriteView,
} from './serviceRemoteModel';
import './hv-modal.css';

interface Props {
  plantId: string;
  clientId: string;
  deviceId: string;
  point: ServicePoint;
  last: ServiceReading | null;
  onWritten(reading: ServiceReading | null): void;
  onClose(): void;
}

interface Result { old: string | null; neu: string | null; oldText: string | null; newText: string | null; protocolId: string; rollback: boolean }

export function ServiceEditModal({ plantId, clientId, deviceId, point: p, last, onWritten, onClose }: Props) {
  const oldRaw = rawValue(last);
  // Zahlen zeigt das Feld mit Komma; valueToWrite nimmt beides.
  const [text, setText] = useState(oldRaw === null ? '' : p.valueType === 'Number' ? withComma(oldRaw) : oldRaw);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<Result | null>(null);

  const problem = text === '' ? null : checkValue(p, text);
  const next = text === '' ? null : valueToWrite(p, text);
  const same = next !== null && oldRaw !== null && next === oldRaw;

  const write = async (value: string, rollbackOf: string | null) => {
    setBusy(true);
    setError(null);
    try {
      const a = await serviceCall<ServiceWriteView>(plantId, clientId, `geraete/${deviceId}/schreiben`,
        { adresse: p.address, wert: value, ruecknahmeVon: rollbackOf });
      const w = a.body;
      if (w.new) onWritten(w.new);
      if (!w.ok) {
        setError(`Der Regler hat abgelehnt: ${w.error ?? 'ohne Grund'}`);
        return;
      }
      setResult({
        old: rawValue(w.old), neu: rawValue(w.new), oldText: w.old?.text ?? null, newText: w.new?.text ?? null,
        protocolId: w.protocolId, rollback: rollbackOf !== null,
      });
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const title = `${p.line ? `${p.line} ` : ''}${p.label || p.address}`;
  return createPortal(
    <div class="hv-modal-backdrop" onClick={() => !busy && onClose()}>
      <div class="hv-modal" onClick={(e) => e.stopPropagation()}>
        <div class="hv-modal__header">
          <h2 class="hv-modal__title">OEM-Datenpunkt · {title}</h2>
          <button type="button" class="hv-modal__close" onClick={onClose} aria-label="Schließen" disabled={busy}>×</button>
        </div>
        <div class="hv-modal__body">
          <p class="hv-modal__hint">{p.group}{p.level ? ` · Ebene ${p.level}` : ''}</p>
          {!result && (
            <>
              {p.valueType === 'Number' && (
                <>
                  <label class="hv-modal__field">
                    <span>Neuer Wert{p.unit ? ` (${p.unit})` : ''}</span>
                    <input inputMode="decimal" value={text} autoFocus
                           onInput={(e) => setText((e.target as HTMLInputElement).value)} />
                  </label>
                  <span class="hv-modal__hint">
                    {p.min != null || p.max != null
                      ? `Bereich ${p.min != null ? withComma(formatPlain(p.min)) : '?'} bis ${p.max != null ? withComma(formatPlain(p.max)) : '?'}${p.unit ? ` ${p.unit}` : ''}. `
                      : ''}
                    {p.step && p.step < 1 ? `Der Regler kennt Stufen von ${stepText(p.step)}; der Wert wird darauf gerundet.` : ''}
                  </span>
                </>
              )}
              {p.valueType === 'Enum' && (
                <label class="hv-modal__field">
                  <span>Auswahl</span>
                  <select value={text} onChange={(e) => setText((e.target as HTMLSelectElement).value)}>
                    {!(p.enum ?? []).some((o) => String(o.value) === text) && <option value="">Bitte wählen</option>}
                    {(p.enum ?? []).map((o) => <option key={o.value} value={String(o.value)}>{o.displayName || o.name}</option>)}
                  </select>
                </label>
              )}
              {p.valueType === 'Bool' && (
                <div class="hv-modal__row">
                  {[['0', 'Aus'], ['1', 'Ein']].map(([v, name]) => (
                    <button key={v} type="button" class={`hv-btn hv-btn--sm${text === v ? ' hv-btn--primary' : ''}`}
                            onClick={() => setText(v!)}>{name}</button>
                  ))}
                </div>
              )}
              {!['Number', 'Enum', 'Bool'].includes(p.valueType) && (
                <label class="hv-modal__field">
                  <span>Neuer Wert</span>
                  <input value={text} onInput={(e) => setText((e.target as HTMLInputElement).value)} />
                </label>
              )}
              <p class="hv-service-edit__compare">
                {displayValue(p, oldRaw, last?.text)} → {next !== null && !problem ? displayValue(p, next) : '?'}
              </p>
              {problem && <p class="hv-modal__error">{problem}</p>}
            </>
          )}
          {result && (
            <p class="hv-service-edit__compare" role="status">
              {result.rollback ? 'Zurückgenommen' : 'Geschrieben'}: {displayValue(p, result.old, result.oldText)} → {displayValue(p, result.neu, result.newText)}
              <span class="hv-modal__hint" style={{ display: 'block', fontWeight: 400 }}>
                Das meldet der Regler jetzt. Die Änderung steht im Schreibprotokoll und in der Befehls-Historie.
              </span>
            </p>
          )}
          {error && <p class="hv-modal__error">{error}</p>}
        </div>
        <div class="hv-modal__footer">
          <span class="hv-modal__footer-spacer" />
          {!result && (
            <>
              <button type="button" class="hv-btn" disabled={busy} onClick={onClose}>Abbrechen</button>
              <button type="button" class="hv-btn hv-btn--primary" disabled={busy || next === null || !!problem || same}
                      onClick={() => next !== null && void write(next, null)}>
                {busy ? 'Schreibe…' : 'Schreiben'}
              </button>
            </>
          )}
          {result && (
            <>
              {!result.rollback && result.old !== null && (
                <button type="button" class="hv-btn" disabled={busy}
                        onClick={() => void write(result.old!, result.protocolId)}>
                  {busy ? 'Nehme zurück…' : 'Rücknahme'}
                </button>
              )}
              <button type="button" class="hv-btn hv-btn--primary" disabled={busy} onClick={onClose}>Fertig</button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
